import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import ReactFlow, { MiniMap, Controls, isNode } from 'react-flow-renderer';
import dagre from 'dagre';
import PropTypes from 'prop-types';
import { MOET_CONSTANTS } from 'moet-core';

import { getQuestionnaires } from '@/selectors/merchant';
import { getAllFeatures } from '@/selectors/featuresConfig';

import { router } from '../configs/router';
import { mapFlowConfig } from './mapper';
import { getBackendFullUrlByRegion, getCountryConfig } from './selectors';

const { ONBOARDING_STATUSES_V2, WIDGET_TYPE } = MOET_CONSTANTS;

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// In order to keep this example simple the node width and height are hardcoded.
// In a real world app you would use the correct width and height values of
// const nodes = useStoreState(state => state.nodes) and then node.__rf.width, node.__rf.height
const nodeWidth = 180;
const nodeHeight = 60;

const getLayoutedElements = (elements, direction = 'TB') => {
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction });

  elements.forEach(el => {
    if (isNode(el)) {
      dagreGraph.setNode(el.id, { width: nodeWidth, height: nodeHeight });
    } else {
      dagreGraph.setEdge(el.source, el.target);
    }
  });

  dagre.layout(dagreGraph);

  return elements.map(el => {
    if (isNode(el)) {
      const nodeWithPosition = dagreGraph.node(el.id);
      el.targetPosition = isHorizontal ? 'left' : 'top';
      el.sourcePosition = isHorizontal ? 'right' : 'bottom';

      // We are shifting the dagre node position (anchor = center center) to top left so it matches
      // the react flow node anchor point (top left).
      el.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
    }

    return el;
  });
};

export const _FlowRenderer = ({
  country,
  countryConfig,
  features,
  questionnaires,
  region,
  showExcludedSteps,
  widgetType,
  getBackendUrlByRegion,
}) => {
  const [reactFlowInstance, setReactFlowInstance] = useState();
  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  const fitInView = () => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView();
    }
  };

  useEffect(() => {
    fitInView();
  }, [country, widgetType, region]);

  const onLoad = reactFlowInstance => {
    setReactFlowInstance(reactFlowInstance);
    reactFlowInstance.fitView();
  };

  const flowConfig = router({
    features,
    isUnmanagedKyc: widgetType === WIDGET_TYPE.UNMANAGED_KYC,
    isWidget: true,
    merchant: { country, status: ONBOARDING_STATUSES_V2.NOT_STARTED },
    questionnaires,
    region,
    widgetType,
    merchantCountryConfig: countryConfig,
  });

  const elements = mapFlowConfig(flowConfig, {
    country,
    hoveredNodeId,
    region,
    showExcludedSteps,
    widgetType,
    getBackendUrlByRegion,
  });
  const layoutedElements = getLayoutedElements(elements);

  const onNodeMouseEnter = (_, { id }) => setHoveredNodeId(id);
  const onNodeMouseLeave = () => setHoveredNodeId(null);

  return (
    <ReactFlow
      elements={layoutedElements}
      onLoad={onLoad}
      onNodeMouseEnter={onNodeMouseEnter}
      onNodeMouseLeave={onNodeMouseLeave}
      // Use the full available height and substract the heights for menu and footer
      style={{ height: 'calc(100vh - 326px)', minHeight: '500px' }}
    >
      <MiniMap />
      <Controls />
    </ReactFlow>
  );
};

_FlowRenderer.propTypes = {
  country: PropTypes.string.isRequired,
  countryConfig: PropTypes.object.isRequired,
  features: PropTypes.object.isRequired,
  getBackendUrlByRegion: PropTypes.func.isRequired,
  questionnaires: PropTypes.object.isRequired,
  region: PropTypes.string.isRequired,
  widgetType: PropTypes.string.isRequired,
  showExcludedSteps: PropTypes.bool,
};

export const FlowRenderer = connect((state, { country, region }) => ({
  countryConfig: getCountryConfig(country, region)(state),
  features: getAllFeatures(state),
  questionnaires: getQuestionnaires(state),
  getBackendUrlByRegion: region => getBackendFullUrlByRegion(state)(region),
}))(_FlowRenderer);
